import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { requestShortCode } from '../../services/shortCodeLogin';
import { AccentButton } from '../components/Button';
import Input from '../components/Input';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const ShortCodeRequestPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setGeneralError(null);
    clearErrors();
    
    try {
      const result = await requestShortCode(data.email);
      // Store challenge_id in sessionStorage to pass to verify page
      sessionStorage.setItem('shortCodeChallengeId', result.challenge_id);
      sessionStorage.setItem('shortCodeEmail', data.email);
      navigate('/short-code-verify');
    } catch (error) {
      const { generalError: apiGeneralError, fieldErrors } = handleApiErrorWithMessages(error);
      
      // Set field-specific errors
      Object.keys(fieldErrors).forEach((field) => {
        setError(field, {
          type: 'server',
          message: fieldErrors[field],
        });
      });
      
      // Set general error if present
      if (apiGeneralError) {
        setGeneralError(apiGeneralError);
      } else if (Object.keys(fieldErrors).length === 0) {
        setGeneralError('Failed to send code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            Sign in with short code
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            We'll send a 6-character code to your email
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* General Error Display */}
          {generalError && (
            <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Email Field */}
            <Input
              type="email"
              label="Email address"
              placeholder="Enter your email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
            />
          </div>

          {/* Submit Button */}
          <div>
            <AccentButton
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Sending code...' : 'Send code'}
            </AccentButton>
          </div>

          {/* Back to Sign In */}
          <div className="text-center">
            <Link
              to="/sign-in"
              className="text-sm text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShortCodeRequestPage;
